import { roofDeckingVerticalThicknessMm } from "../roof/roofDecking";
import type { ChimneyResult, MaterialRow } from '../../engines/types';
import type { Market } from '../location/types';
import { getT } from '../../i18n';
import {
  CHIMNEY_INNER_DIAMETER_MM,
  CHIMNEY_MAX_TOTAL_HEIGHT_MM,
  DEFAULT_FLUE_OUTLET_HEIGHT_MM,
  planChimneyStack,
} from './chimneyStack';

/**
 * Vertical build-up between the top of the wall plate (the line `ceilingMm`
 * measures up to) and the top of the finished roof covering. The chimney has
 * to clear the roof SURFACE, not the structural roof line, so this stack sits
 * between the ceiling and the required termination height.
 *
 * Uses the shared nominal decking specification, projected vertically at the
 * roof pitch. Joist depth and covering remain conservative catalogue defaults;
 * callers with a resolved assembly should provide roofBuildupMm explicitly.
 * Keep the previous 260 mm allowance as a floor to avoid shortening quotes.
 */
function defaultRoofBuildupMm(roofAngleDeg: number): number {
  return Math.max(260, Math.ceil(220 + roofDeckingVerticalThicknessMm(roofAngleDeg * Math.PI / 180) + 4 + 12));
}

/**
 * Swedish rule (Boverket's allmänna råd; Contura and Harvia manuals say the
 * same): a chimney for a stove up to 60 kW should "dels mynna över nock, dels
 * minst 1,0 meter över taktäckningen". The 1.0 m is measured from the roof
 * COVERING where the chimney passes through it, which is why the roof
 * build-up is part of the height. Without a known flue position the 1.0 m is
 * taken at the ridge, which is never lower.
 */
const ABOVE_COVERING_MM = 1000;
/**
 * "Över nock": when the flue sits far enough down the slope that 1.0 m above
 * the covering there is still below the ridge, the outlet clears the ridge
 * covering by this much — enough to stand over the ridge cap and flashing.
 */
const ABOVE_RIDGE_MIN_MM = 100;

export interface CalculateChimneyInput {
  /** Finished floor to the top of the wall plate (interior ceiling line). */
  ceilingMm: number;
  roofAngleDeg: number;
  /**
   * Horizontal run the pitch is applied over, eave → ridge/high edge.
   * Gable: half the WIDTH. Mono/shed: the full slope run — the LENGTH for a
   * front/back high wall, the WIDTH for a left/right one
   * (`getRoofSlopeRunM` in `rectRoofGeometry`).
   */
  roofSpanMm?: number;
  /**
   * Roof rise at the flue, eave → where the chimney passes the roof (same
   * datum as the rise over `roofSpanMm`). Given, the 1.0 m is measured there
   * and the outlet need only clear the ridge; omitted, it is measured at the
   * ridge.
   */
  flueRoofRiseMm?: number;
  /** Wall plate → top of the finished roof covering. See the constant above. */
  roofBuildupMm?: number;
  /** Finished floor → stove flue collar. See the constant above. */
  flueOutletHeightMm?: number;
  /**
   * Indoor sauna (issue 175): sauna ceiling → the house's ridge. The flue
   * leaves through the HOUSE roof, so this replaces the sauna's own roof
   * build-up and rise. `houseAboveAssumed` marks a default the user never
   * confirmed, and the result says so.
   */
  houseAboveCeilingMm?: number;
  houseAboveAssumed?: boolean;
}

function createChimneyMaterial(material: string, dimension: string, quantity: string, priceRuleId?: string, materialKey?: string): MaterialRow {
  return {
    material,
    dimension,
    quantity,
    category: 'chimney',
    phase: 'systems',
    ...(priceRuleId ? { priceRuleId } : {}),
    ...(materialKey ? { materialKey } : {}),
  };
}

/**
 * Floor → wall plate → roof covering where the flue passes it → 1 000 mm above
 * it, and at least clear of the ridge. Without a flue position the covering is
 * taken at the ridge.
 * Indoors: floor → sauna ceiling → through the house to its ridge → 1 000 mm.
 * The 3D model reads the same height, so it draws the modules the quote buys.
 */
export function calculateChimneyHeightMm({
  ceilingMm,
  roofAngleDeg,
  roofSpanMm = 1250,
  flueRoofRiseMm,
  roofBuildupMm = defaultRoofBuildupMm(roofAngleDeg),
  houseAboveCeilingMm,
}: CalculateChimneyInput): number {
  if (houseAboveCeilingMm !== undefined) {
    return ceilingMm + houseAboveCeilingMm + ABOVE_COVERING_MM;
  }
  const ridgeRiseMm = Math.round(Math.tan((roofAngleDeg * Math.PI) / 180) * roofSpanMm);
  const aboveWallPlateMm =
    flueRoofRiseMm === undefined
      ? ridgeRiseMm + ABOVE_COVERING_MM
      : Math.max(
          Math.min(Math.round(flueRoofRiseMm), ridgeRiseMm) + ABOVE_COVERING_MM,
          ridgeRiseMm + ABOVE_RIDGE_MIN_MM,
        );
  return ceilingMm + roofBuildupMm + aboveWallPlateMm;
}

/**
 * `heightMm` is the TOTAL chimney height measured from the finished sauna
 * floor to the flue outlet — the number surfaced as "Total höjd" in PricePane,
 * BBRPane, the PDF and the build guide. It is NOT the length of chimney to
 * buy: the smoke pipe carries the run from the stove collar to the insulated
 * chimney, and the sections come from `planChimneyStack`.
 */
export function calculateChimney(input: CalculateChimneyInput, market: Market = 'se'): ChimneyResult {
  const { ceilingMm, houseAboveCeilingMm, houseAboveAssumed = false, flueOutletHeightMm = DEFAULT_FLUE_OUTLET_HEIGHT_MM } = input;
  const t = getT(market);
  const heightMm = calculateChimneyHeightMm(input);
  const houseAboveM = houseAboveCeilingMm === undefined ? '' : (houseAboveCeilingMm / 1000).toFixed(1);
  const stack = planChimneyStack(heightMm, ceilingMm, flueOutletHeightMm);
  const { extensionsLong, extensionsShort } = stack;
  const runMm = stack.topMm - stack.flueOutletHeightMm;
  // Through a tall house the run outgrows the sauna chimney: say so rather
  // than quote a stack the manufacturer does not allow.
  const overMaxNote =
    runMm > CHIMNEY_MAX_TOTAL_HEIGHT_MM
      ? ` ${t.domain_chimney_overMaxHeight.replace('{m}', (runMm / 1000).toFixed(1))}`
      : '';
  const materials = [
    createChimneyMaterial(t.domain_chimney_kit, t.domain_chimney_dimension, '1 st', 'prices.bastuskorsten', 'chimney.kit'),
    ...(extensionsLong > 0
      ? [createChimneyMaterial(t.domain_chimney_extensionLong, t.domain_chimney_dimension, `${extensionsLong} st`, 'prices.bastuskorstenForlangning', 'chimney.extension-1000')]
      : []),
    ...(extensionsShort > 0
      ? [createChimneyMaterial(t.domain_chimney_extensionShort, t.domain_chimney_dimension, `${extensionsShort} st`, 'prices.bastuskorstenForlangningKort', 'chimney.extension-500')]
      : []),
    createChimneyMaterial(t.domain_chimney_fireSealInsulation, t.domain_chimney_dimension, '1 st', 'prices.brandtatning', 'chimney.fire-seal'),
  ];

  return {
    diameterMm: CHIMNEY_INNER_DIAMETER_MM,
    heightMm,
    recommendedSystem: t.domain_chimney_recommendedSystem,
    clearanceRule: t.domain_chimney_clearanceRule,
    maintenanceNote: t.domain_chimney_maintenanceNote,
    ...(houseAboveCeilingMm === undefined
      ? {}
      : {
          throughHouseNote:
            (houseAboveAssumed
              ? t.domain_chimney_throughHouseAssumed
              : t.domain_chimney_throughHouse
            ).replace('{m}', houseAboveM) + overMaxNote,
        }),
    materials,
  };
}
